import { FIRST_PAGE as ONE, FIRST_ITEM_INDEX as ZERO } from '@/constants';
import escapeRegExp from 'lodash/escapeRegExp';
import React from 'react';
import {
  Address,
  Column,
  ListingIds,
  SearchResultItemWrapper,
  SearchResultsHeaderWrapper,
  SearchResultsTitle,
  Spacer,
} from './search-styles';

const NO_MATCHES = ZERO;
const SINGLE_ITEM = ONE;
const SINGLE_MATCH = ONE;
const EMPTY_RESULT = '';

function SearchResultsHeader() {
  return (
    <SearchResultsHeaderWrapper>
      <Address>Address</Address>
      <Spacer />
      <Column style={{ width: '200px;' }}>APN</Column>
      <Spacer />
      <Column>License</Column>
      <Spacer />
      <ListingIds>Listing Ids</ListingIds>
    </SearchResultsHeaderWrapper>
  );
}

function SearchFallbackMessage() {
  return <div data-cy="search-fallback-message">No results to display</div>;
}

const getStringWithHighlightedTerm = (
  originalString: string,
  pattern: string
) => {
  if (!originalString) return ''; // apn could be null
  if (!pattern) return originalString;

  const escapedPattern = escapeRegExp(pattern);

  return originalString.replace(
    new RegExp(escapedPattern, 'gi'),
    (match) => `<strong>${match}</strong>`
  );
};

export const highlightMatchedSubstring = (
  originalString: string,
  pattern: string
) => {
  return { __html: getStringWithHighlightedTerm(originalString, pattern) };
};

function multipleResults(resultArray: string[], searchParam: string) {
  const firstMatchHtml = getStringWithHighlightedTerm(
    resultArray[0],
    searchParam
  );
  return `${firstMatchHtml}, +${resultArray.length - ONE}`;
}

function selectListingIdToDisplay(
  listingIds: string[] | null | undefined,
  searchParam: string
) {
  // find if listingId matches searchParam
  const matchedListingIds = listingIds?.filter((id) =>
    id.includes(searchParam)
  );

  let listingIdResult = null;

  if (!listingIds) {
    listingIdResult = EMPTY_RESULT;
  } else if (!matchedListingIds || matchedListingIds.length === NO_MATCHES) {
    listingIdResult =
      listingIds.length > SINGLE_ITEM
        ? multipleResults(listingIds, searchParam)
        : listingIds[0] || EMPTY_RESULT;
  } else {
    listingIdResult =
      matchedListingIds.length > SINGLE_MATCH
        ? multipleResults(matchedListingIds, searchParam)
        : getStringWithHighlightedTerm(matchedListingIds[0], searchParam);
  }
  return { __html: listingIdResult };
}

export interface SearchResult {
  address: string;
  apn: string;
  deckardid: unknown;
  strLicense: string;
  listingIds: string[] | null | undefined;
}

interface SearchResultItemProps {
  handleResultOnClick: (value: string) => void;
  result: SearchResult;
  searchParam: string;
}

function SearchResultItem(props: SearchResultItemProps) {
  const {
    searchParam,
    result: { address, apn, deckardid, strLicense, listingIds },
    handleResultOnClick,
  } = props;

  return (
    <SearchResultItemWrapper
      data-cy="universal-search-results"
      onClick={() => handleResultOnClick(String(deckardid))}
    >
      <Address
        data-cy="universal-search-results-address"
        dangerouslySetInnerHTML={highlightMatchedSubstring(
          address,
          searchParam
        )}
      />
      <Spacer />
      <Column
        style={{ width: '200px;' }}
        dangerouslySetInnerHTML={highlightMatchedSubstring(apn, searchParam)}
      />
      <Spacer />
      <Column
        dangerouslySetInnerHTML={highlightMatchedSubstring(
          strLicense,
          searchParam
        )}
      />
      <Spacer />
      <ListingIds
        dangerouslySetInnerHTML={selectListingIdToDisplay(
          listingIds,
          searchParam
        )}
      />
    </SearchResultItemWrapper>
  );
}

interface Props {
  handleResultOnClick: (value: string) => void;
  searchParam: string;
  searchResults: SearchResult[];
}

export function SearchResults(props: Props) {
  const { handleResultOnClick, searchParam, searchResults } = props;

  if (searchResults.length === NO_MATCHES) {
    return <SearchFallbackMessage />;
  }

  return (
    <React.Fragment>
      <SearchResultsTitle />
      <SearchResultsHeader />
      {searchResults.map((onePropertyData, idx) => (
        <SearchResultItem
          key={idx}
          searchParam={searchParam}
          result={onePropertyData}
          handleResultOnClick={handleResultOnClick}
        />
      ))}
    </React.Fragment>
  );
}
